// Form validation utilities
export const ValidationRules = {
  // Project name validation
  projectName: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-_.]+$/,
    message: 'Project name must be 2-100 characters and contain only letters, numbers, spaces, hyphens, underscores, and periods'
  },
  
  // Project description validation
  projectDescription: {
    required: false,
    maxLength: 500,
    message: 'Project description cannot exceed 500 characters'
  },
  
  // Email validation
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  
  // Password validation
  password: {
    required: true,
    minLength: 8,
    maxLength: 128,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])/,
    message: 'Password must be at least 8 characters with at least one lowercase, uppercase, number, and special character'
  },
  
  // Search query validation
  searchQuery: {
    required: false,
    minLength: 1,
    maxLength: 200,
    pattern: /^[a-zA-Z0-9\s\-_.]+$/,
    message: 'Search query must contain only letters, numbers, spaces, hyphens, underscores, and periods'
  },

  // Contact form validations
  firstName: {
    required: true,
    minLength: 1,
    maxLength: 50,
    pattern: /^[a-zA-Z\s\-']+$/,
    message: 'First name must be 1-50 characters and contain only letters, spaces, hyphens, and apostrophes'
  },

  lastName: {
    required: true,
    minLength: 1,
    maxLength: 50,
    pattern: /^[a-zA-Z\s\-']+$/,
    message: 'Last name must be 1-50 characters and contain only letters, spaces, hyphens, and apostrophes'
  },

  jobTitle: {
    required: true,
    minLength: 1,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-_.,'&/()]+$/,
    message: 'Job title must be 1-100 characters'
  },

  companyName: {
    required: true,
    minLength: 1,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-_.,'&/()]+$/,
    message: 'Company name must be 1-100 characters'
  },

  contactSubject: {
    required: true,
    enum: ['Demo', 'Enterprise quote', 'Technical Support', 'Partnership', 'Other'],
    message: 'Please select a valid subject'
  },

  contactMessage: {
    required: true,
    minLength: 10,
    maxLength: 2000,
    message: 'Message must be 10-2000 characters'
  }
};

// Sanitization functions
export const sanitize = {
  // Remove HTML tags and trim whitespace
  text: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/[<>]/g, '')
      .trim();
  },
  
  // Sanitize project names (no trim - allow trailing spaces while typing)
  projectName: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/[^\w\s\-_.]/g, '')
      .substring(0, 100);
  },
  
  // Sanitize descriptions (no trim - allow trailing spaces while typing)
  description: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .substring(0, 500);
  },
  
  // Sanitize email
  email: (value) => {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .trim()
      .substring(0, 254);
  },
  
  // Sanitize search queries
  search: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/[^\w\s\-_.]/g, '')
      .trim()
      .substring(0, 200);
  },

  // Sanitize name fields
  name: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/[^a-zA-Z\s\-']/g, '')
      .substring(0, 50);
  },

  // Sanitize job title or company name
  businessText: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .substring(0, 100);
  },

  // Sanitize message/long text
  message: (value) => {
    if (!value) return '';
    return String(value)
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .substring(0, 2000);
  }
};

// Validation function
export const validateField = (value, rules) => {
  const sanitizedValue = sanitize.text(value);

  // Required field check
  if (rules.required && (!sanitizedValue || sanitizedValue.length === 0)) {
    return 'This field is required';
  }

  // Skip other validations if field is empty and not required
  if (!sanitizedValue && !rules.required) {
    return null;
  }

  // Enum validation (for select fields)
  if (rules.enum && !rules.enum.includes(sanitizedValue)) {
    return rules.message || 'Invalid selection';
  }

  // Length validations
  if (rules.minLength && sanitizedValue.length < rules.minLength) {
    return `Must be at least ${rules.minLength} characters`;
  }

  if (rules.maxLength && sanitizedValue.length > rules.maxLength) {
    return `Cannot exceed ${rules.maxLength} characters`;
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(sanitizedValue)) {
    return rules.message || 'Invalid format';
  }

  return null;
};

// Validate entire form
export const validateForm = (formData, validationSchema) => {
  const errors = {};
  let isValid = true;
  
  Object.keys(validationSchema).forEach(field => {
    const error = validateField(formData[field], validationSchema[field]);
    if (error) {
      errors[field] = error;
      isValid = false;
    }
  });
  
  return { isValid, errors };
};

// Password strength checker
export const checkPasswordStrength = (password) => {
  if (!password) return { score: 0, strength: 'weak', feedback: ['Password is required'] };
  
  const feedback = [];
  let score = 0;
  
  // Length check
  if (password.length >= 8) score += 1;
  else feedback.push('Use at least 8 characters');
  
  // Character variety checks
  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Include lowercase letters');
  
  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('Include uppercase letters');
  
  if (/\d/.test(password)) score += 1;
  else feedback.push('Include numbers');
  
  if (/[^A-Za-z\d\s]/.test(password)) score += 1;
  else feedback.push('Include special characters (e.g. !@#$%^&*-)');
  
  // Length bonus
  if (password.length >= 12) score += 1;
  
  const strength = score <= 2 ? 'weak' : score <= 4 ? 'medium' : 'strong';
  
  return { score, strength, feedback };
};

